import React, { useState } from "react";

// ==================== Styles ====================
// The styles object must be defined BEFORE the component that uses it.
const styles = {
  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 20px", // More horizontal padding
    borderTop: "1px solid #eee", // Light, clean border
    marginTop: 20,
    backgroundColor: "#ffffff",
    minHeight: 60,
    fontFamily: "Arial, sans-serif", // Use a clean font
    borderRadius: "16px",
  } as React.CSSProperties,

  resultsPerPageControl: {
    display: "flex",
    alignItems: "center",
  } as React.CSSProperties,

  resultsPerPageSelect: {
    padding: "5px 10px",
    borderRadius: 4,
    border: "1px solid #ddd", // Lighter border
    backgroundColor: "#fff",
    cursor: "pointer",
    fontSize: 13,
    height: 34, // Match button height
    color: "#333",
    // Clean up browser default appearance
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    paddingRight: "30px", // Space for custom arrow
    // Custom SVG arrow to match the 'v' in the image
    backgroundImage: `url('data:image/svg+xml;utf8,<svg fill="%23555" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/><path d="M0 0h24v24H0z" fill="none"/></svg>')`,
    backgroundRepeat: "no-repeat",
    backgroundPositionX: "98%",
    backgroundPositionY: "50%",
    backgroundSize: "20px",
  } as React.CSSProperties,

  pageNumbersGroup: {
    display: "flex",
    alignItems: "center",
    userSelect: "none",
  } as React.CSSProperties,

  pageNumber: (isActive: boolean, isEllipsis: boolean = false): React.CSSProperties =>
    ({
      padding: isEllipsis ? "5px 4px" : "5px 10px", // Padding for click target
      margin: "0 2px",
      border: "none", // No border
      // THE KEY CHANGE: Use borderBottom for the yellow underline
      borderBottom: isActive ? "3px solid #ffc107" : "3px solid transparent",
      borderRadius: 0, // No border radius
      cursor: isEllipsis ? "default" : "pointer",
      fontSize: 14,
      fontWeight: isActive ? 600 : 400, // Active is bolder
      color: isActive ? "#222" : "#666",
      transition: "all 0.2s",
      lineHeight: "22px",
      minWidth: isEllipsis ? "auto" : 20,
      textAlign: "center",
    } as React.CSSProperties),

  paginationArrow: (isDisabled: boolean): React.CSSProperties =>
    ({
      // This is now just text, not a button
      padding: "5px 10px",
      margin: "0 5px",
      border: "none", // No border
      borderRadius: 0,
      cursor: isDisabled ? "default" : "pointer",
      color: isDisabled ? "#ccc" : "#555", // Darker arrow
      backgroundColor: "transparent", // No background
      transition: "color 0.2s",
      lineHeight: "22px",
      fontSize: 16, // Make arrows easy to see
      fontWeight: "bold",
    } as React.CSSProperties),

  goToControl: {
    display: "flex",
    alignItems: "center",
  } as React.CSSProperties,

  goToInput: {
    width: 45,
    padding: "5px 8px",
    border: "1px solid #ddd", // Lighter border
    borderRadius: 4,
    textAlign: "center",
    marginRight: 8, // More space
    fontSize: 13,
    height: 34, // Match button height
    boxSizing: "border-box", // Correct height calculation
  } as React.CSSProperties,

  goToButton: {
    padding: "5px 12px",
    backgroundColor: "#ffc107",
    border: "1px solid #ffc107",
    borderRadius: 4,
    color: "#333",
    cursor: "pointer",
    fontWeight: 600, // Bolder
    fontSize: 13,
    height: 34, // Standard height
    transition: "background-color 0.2s",
  } as React.CSSProperties,

  labelText: {
    marginRight: 8,
    color: "#666",
    fontSize: 13,
  } as React.CSSProperties,
};

// ==================== Component Definition ====================

interface PaginationControlsProps {
  totalItems: number;
  currentPage: number;
  resultsPerPage: number;
  onPageChange: (page: number) => void;
  onResultsPerPageChange: (perPage: number) => void;
}

const RESULTS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100, 200];

const PaginationControls: React.FC<PaginationControlsProps> = ({
  totalItems,
  currentPage,
  resultsPerPage,
  onPageChange,
  onResultsPerPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / resultsPerPage);
  const [goToPage, setGoToPage] = useState("");

  // if (totalItems === 0 || totalPages <= 1) {
  //   return null;
  // }

  const handleGoTo = () => {
    const page = parseInt(goToPage);
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      setGoToPage("");
    } else {
      // Note: alert() is disabled in this environment.
      // In a real app, you'd show a small error message.
      console.warn(`Invalid page number. Please enter 1-${totalPages}`);
      setGoToPage("");
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow && totalPages >= maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    // Left arrow
    pageNumbers.push(
      <span
        key="prev"
        style={styles.paginationArrow(currentPage === 1)}
        onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
      >
        &lt;
      </span>
    );

    // Page links
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <span key={i} style={styles.pageNumber(i === currentPage)} onClick={() => onPageChange(i)}>
          {i}
        </span>
      );
    }

    // Ellipsis
    if (endPage < totalPages) {
      pageNumbers.push(
        <span key="ellipsis" style={styles.pageNumber(false, true)}>
          ...
        </span>
      );
    }

    // Right arrow
    pageNumbers.push(
      <span
        key="next"
        style={styles.paginationArrow(currentPage === totalPages)}
        onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
      >
        &gt;
      </span>
    );

    return pageNumbers;
  };

  return (
    <div style={styles.paginationContainer}>
      {/* Results per page control */}
      <div style={styles.resultsPerPageControl}>
        <span style={styles.labelText}>Results per page</span>
        <select
          style={styles.resultsPerPageSelect}
          value={resultsPerPage}
          onChange={(e) => onResultsPerPageChange(Number(e.target.value))}
        >
          {RESULTS_PER_PAGE_OPTIONS.map((num) => (
            <option key={num} value={num}>
              {num}
            </option>
          ))}
        </select>
      </div>

      {/* Page numbers */}
      <div style={styles.pageNumbersGroup}>{renderPageNumbers()}</div>

      {/* Go to page control */}
      <div style={styles.goToControl}>
        <span style={styles.labelText}>Go to:</span>
        <input
          type="number"
          value={goToPage}
          onChange={(e) => setGoToPage(e.target.value)}
          style={styles.goToInput}
          min="1"
          max={totalPages}
        />
        <button style={styles.goToButton} onClick={handleGoTo}>
          Go
        </button>
      </div>
    </div>
  );
};

export default PaginationControls;
